import { currentLocale } from "../i18n";

/**
 * The countries a user, a congregation or a circumscription can sit in: ISO
 * 3166-1 alpha-2, the same list the old application offered through the
 * country_select gem and the same two letters it wrote to the database.
 *
 * Only the codes are kept here. The names come from Intl.DisplayNames, which
 * reads the browser's own CLDR data and therefore answers in whatever language
 * the interface is in, without a translation table of 249 countries times three
 * languages having to be carried in this repository and kept up to date.
 */
export const COUNTRY_CODES: readonly string[] = [
  "AD", "AE", "AF", "AG", "AI", "AL", "AM", "AO", "AQ", "AR", "AS", "AT", "AU", "AW", "AX",
  "AZ", "BA", "BB", "BD", "BE", "BF", "BG", "BH", "BI", "BJ", "BL", "BM", "BN", "BO", "BQ",
  "BR", "BS", "BT", "BV", "BW", "BY", "BZ", "CA", "CC", "CD", "CF", "CG", "CH", "CI", "CK",
  "CL", "CM", "CN", "CO", "CR", "CU", "CV", "CW", "CX", "CY", "CZ", "DE", "DJ", "DK", "DM",
  "DO", "DZ", "EC", "EE", "EG", "EH", "ER", "ES", "ET", "FI", "FJ", "FK", "FM", "FO", "FR",
  "GA", "GB", "GD", "GE", "GF", "GG", "GH", "GI", "GL", "GM", "GN", "GP", "GQ", "GR", "GS",
  "GT", "GU", "GW", "GY", "HK", "HM", "HN", "HR", "HT", "HU", "ID", "IE", "IL", "IM", "IN",
  "IO", "IQ", "IR", "IS", "IT", "JE", "JM", "JO", "JP", "KE", "KG", "KH", "KI", "KM", "KN",
  "KP", "KR", "KW", "KY", "KZ", "LA", "LB", "LC", "LI", "LK", "LR", "LS", "LT", "LU", "LV",
  "LY", "MA", "MC", "MD", "ME", "MF", "MG", "MH", "MK", "ML", "MM", "MN", "MO", "MP", "MQ",
  "MR", "MS", "MT", "MU", "MV", "MW", "MX", "MY", "MZ", "NA", "NC", "NE", "NF", "NG", "NI",
  "NL", "NO", "NP", "NR", "NU", "NZ", "OM", "PA", "PE", "PF", "PG", "PH", "PK", "PL", "PM",
  "PN", "PR", "PS", "PT", "PW", "PY", "QA", "RE", "RO", "RS", "RU", "RW", "SA", "SB", "SC",
  "SD", "SE", "SG", "SH", "SI", "SJ", "SK", "SL", "SM", "SN", "SO", "SR", "SS", "ST", "SV",
  "SX", "SY", "SZ", "TC", "TD", "TF", "TG", "TH", "TJ", "TK", "TL", "TM", "TN", "TO", "TR",
  "TT", "TV", "TW", "TZ", "UA", "UG", "UM", "US", "UY", "UZ", "VA", "VC", "VE", "VG", "VI",
  "VN", "VU", "WF", "WS", "YE", "YT", "ZA", "ZM", "ZW",
];

/**
 * What is offered before the alphabet starts: Switzerland, then the five
 * countries it borders on, largest population first. Nearly every address in
 * this application is one of these, and scrolling to "Schweiz" past two hundred
 * others is the kind of small friction a coordinator meets on every screen that
 * has an address on it.
 */
export const PRIORITY_COUNTRY_CODES: readonly string[] = ["CH", "DE", "FR", "IT", "AT", "LI"];

export interface CountryOption {
  value: string;
  label: string;
}

// Built once per language: the whole list is named on every render of a form
// that has a country on it, and a fresh formatter for each of 249 codes is the
// expensive part of that.
const displayNames = new Map<string, Intl.DisplayNames | null>();

function namesFor(locale: string): Intl.DisplayNames | null {
  if (!displayNames.has(locale)) {
    try {
      const names = new Intl.DisplayNames([locale], { type: "region", fallback: "code" });
      displayNames.set(locale, names);
    } catch {
      // An engine without the region data. The codes still say which country
      // is meant, which is more useful than an empty list.
      displayNames.set(locale, null);
    }
  }

  return displayNames.get(locale) ?? null;
}

/** The name of a country in the interface language, or the code if it has none. */
export function countryName(code: string, locale: string = currentLocale()): string {
  try {
    return namesFor(locale)?.of(code) ?? code;
  } catch {
    // Not a region code at all — the free text the address forms used to take
    // makes Intl.DisplayNames throw, and it has to survive being read.
    return code;
  }
}

/**
 * The whole list, in the order it is offered: the priority block in its own
 * order, then everything else by name in the language being read — "Ägypten"
 * sorts where a German reader looks for it, "Egitto" where an Italian one does.
 */
export function countryOptions(locale: string = currentLocale()): CountryOption[] {
  const named = (value: string) => ({ value, label: countryName(value, locale) });

  const priority = PRIORITY_COUNTRY_CODES.map(named);
  const rest = COUNTRY_CODES.filter((code) => !PRIORITY_COUNTRY_CODES.includes(code))
    .map(named)
    .sort((a, b) => a.label.localeCompare(b.label, locale));

  return [...priority, ...rest];
}
